import { useCallback, useRef } from 'react';
import useEvent from 'react-use/lib/useEvent';
import useUnmount from 'react-use/lib/useUnmount';

type UseScrollContainerEventsOptions = {
  acceptScrollContainer: (scrollContainer: Element) => boolean;
  onScrollContainer: (scrollContainer: Element) => void;
};

const getScrollContainer = (target: EventTarget | null): Element | null => {
  if (target === document) {
    return document.scrollingElement;
  }

  return target instanceof Element ? target : null;
};

export const useScrollContainerEvents = ({
  acceptScrollContainer,
  onScrollContainer,
}: UseScrollContainerEventsOptions): void => {
  const pendingScrollContainersRef = useRef(new Set<Element>());
  const animationFrameRef = useRef<number>();

  const processPendingScrollContainers = useCallback((): void => {
    animationFrameRef.current = undefined;
    pendingScrollContainersRef.current.forEach(scrollContainer =>
      onScrollContainer(scrollContainer),
    );
    pendingScrollContainersRef.current.clear();
  }, [onScrollContainer]);

  const handleScroll = useCallback(
    (event: Event): void => {
      const scrollContainer = getScrollContainer(event.target);
      if (!scrollContainer || !acceptScrollContainer(scrollContainer)) return;

      pendingScrollContainersRef.current.add(scrollContainer);
      if (animationFrameRef.current !== undefined) return;

      animationFrameRef.current = window.requestAnimationFrame(
        processPendingScrollContainers,
      );
    },
    [acceptScrollContainer, processPendingScrollContainers],
  );

  const scrollTarget = typeof document === 'undefined' ? null : document;
  useEvent('scroll', handleScroll, scrollTarget, true);
  useUnmount(() => {
    if (animationFrameRef.current !== undefined) {
      window.cancelAnimationFrame(animationFrameRef.current);
    }
    pendingScrollContainersRef.current.clear();
  });
};
